import { T } from '@tolgee/react';
import { Tooltip } from 'antd';
import Link from 'next/link';
import { FunctionComponent, useState } from 'react';
import styled from 'styled-components';

import theme from '../../../assets/theme';

interface TeachingCreditCounterProps {
  remaining: number;
  bonusRemaining: number;
  weeklyRate: number;
  used: number;
  renewsAt?: string;
  onGetMore?: () => void;
  unlimited?: boolean;
}

const STARTING_TOTAL = 10;
const BONUS_COLOR = '#E8B86D';
const WEEKLY_COLOR = theme.colors.orange;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 4px;
  margin-top: 6px;
  border-top: 1px solid #e0d8d0;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${theme.colors.greyDark};
  line-height: 1;
`;

const BarTrack = styled.div`
  width: 100%;
  height: 6px;
  background: #e8e0d8;
  border-radius: 3px;
  overflow: hidden;
  display: flex;
`;

const BarSegment = styled.div<{
  $width: number;
  $color: string;
  $dimmed?: boolean;
}>`
  height: 100%;
  width: ${({ $width }) => $width}%;
  background: ${({ $color }) => $color};
  transition: width 0.3s ease, opacity 0.15s ease;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.3 : 1)};
`;

const EmptySegment = styled.div<{ $width: number }>`
  height: 100%;
  flex: 1;
  min-width: ${({ $width }) => $width}%;
`;

const Sublabel = styled.span`
  font-size: 11px;
  color: ${theme.colors.greyDark};
  opacity: 0.6;
  line-height: 1;
`;

const formatRenewalDay = (renewsAt: string) => {
  try {
    const date = new Date(renewsAt);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return 'Sunday';
  }
};

export const TeachingCreditCounter: FunctionComponent<TeachingCreditCounterProps> = ({
  remaining,
  bonusRemaining,
  weeklyRate,
  used,
  renewsAt,
  onGetMore,
  unlimited,
}) => {
  const [hovered, setHovered] = useState<'bonus' | 'weekly' | 'empty' | null>(null);

  const renewalDay = renewsAt ? formatRenewalDay(renewsAt) : 'Monday';
  const hasSessions = remaining > 0;

  const weeklyUsed = Math.min(used, weeklyRate);
  const weeklyRemaining = weeklyRate - weeklyUsed;

  const bonusPercent = (bonusRemaining / STARTING_TOTAL) * 100;
  const weeklyPercent = (weeklyRemaining / STARTING_TOTAL) * 100;
  const emptyPercent = 100 - bonusPercent - weeklyPercent;

  const getTooltipText = () => {
    if (hovered === 'bonus') {
      return `${bonusRemaining} bonus sessions remaining`;
    }
    if (hovered === 'weekly') {
      return `${weeklyRemaining} of ${weeklyRate} weekly sessions remaining`;
    }
    if (hovered === 'empty' && bonusRemaining === 0) {
      return '0 bonus sessions remaining';
    }
    return '';
  };

  const tooltipText = getTooltipText();

  const handleClick = onGetMore
    ? (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onGetMore();
      }
    : undefined;

  if (unlimited) {
    return (
      <Link href="/profile" passHref>
        <a style={{ textDecoration: 'none' }}>
          <Container>
            <Label>
              <T
                keyName="LessonCreditCounter.Unlimited"
                defaultValue="∞ Unlimited teaching sessions"
              />
            </Label>
            <BarTrack>
              <BarSegment $width={100} $color={WEEKLY_COLOR} />
            </BarTrack>
            <Sublabel>
              <T
                keyName="LessonCreditCounter.IncludedInPlan"
                defaultValue="Included in your plan"
              />
            </Sublabel>
          </Container>
        </a>
      </Link>
    );
  }

  return (
    <Link href="/profile" passHref>
      <a style={{ textDecoration: 'none' }} onClick={handleClick}>
        <Container>
          <Label>
            {hasSessions ? (
              <T
                keyName="LessonCreditCounter.SessionsLeft"
                defaultValue="{remaining} teaching {remaining, plural, one {session} other {sessions}} left"
                params={{ remaining }}
              />
            ) : (
              <T
                keyName="LessonCreditCounter.NoSessionsLeft"
                defaultValue="No teaching sessions left"
              />
            )}
          </Label>

          <Tooltip
            title={tooltipText}
            visible={!!tooltipText && hovered !== null}
            placement="right"
          >
            <BarTrack onMouseLeave={() => setHovered(null)}>
              {bonusPercent > 0 && (
                <BarSegment
                  $width={bonusPercent}
                  $color={BONUS_COLOR}
                  $dimmed={hovered !== null && hovered !== 'bonus'}
                  onMouseEnter={() => setHovered('bonus')}
                />
              )}
              {weeklyPercent > 0 && (
                <BarSegment
                  $width={weeklyPercent}
                  $color={WEEKLY_COLOR}
                  $dimmed={hovered !== null && hovered !== 'weekly'}
                  onMouseEnter={() => setHovered('weekly')}
                />
              )}
              {emptyPercent > 0 && bonusRemaining === 0 && (
                <EmptySegment
                  $width={emptyPercent}
                  onMouseEnter={() => setHovered('empty')}
                />
              )}
            </BarTrack>
          </Tooltip>

          <Sublabel>
            {hasSessions ? (
              <T
                keyName="LessonCreditCounter.Renews"
                defaultValue="{weeklyRate} per week · resets {renewalDay}"
                params={{ weeklyRate, renewalDay }}
              />
            ) : (
              <T keyName="LessonCreditCounter.GetMore" defaultValue="Get more" />
            )}
          </Sublabel>
        </Container>
      </a>
    </Link>
  );
};
